import {
  ExportOutlined,
  SearchOutlined,
  SettingOutlined
} from "@ant-design/icons"
import { Button, Flex, Radio, Skeleton, Space, Tag, Typography } from "antd"
import { type CheckboxOptionType } from "antd/es/checkbox/Group"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { SearchBar } from "../../../../../../../components/Input/SearchBar.tsx"
import { ENTRY_TYPE_OPTIONS } from "../../../../constants/finance-item-constants.ts"
import { type ProjectFinancialTransactionFilters } from "../../../../../redux/project.types.ts"
import { type ProjectSubcontractor } from "../../../../../types/project-subcontractors.types.tsx"

interface ProjectViewFinanceHeaderProps {
  selectedSubcontractor: ProjectSubcontractor | null
  filters: ProjectFinancialTransactionFilters
  isEmptyList: boolean
  options: Record<string, BaseOptionType[]>
  isLocked?: boolean
  fetchingOptions: boolean
  onFilterChange: (data: Partial<ProjectFinancialTransactionFilters>) => void
  onExport: () => Promise<void>
  onClearSubcontractor: () => void
  onOpenColumnConfig: () => void
}

export function ProjectViewFinanceHeader({
  selectedSubcontractor,
  filters,
  options,
  isEmptyList,
  isLocked = false,
  fetchingOptions,
  onFilterChange,
  onExport,
  onClearSubcontractor,
  onOpenColumnConfig
}: ProjectViewFinanceHeaderProps) {
  const { t } = useTranslation()
  const hasAdditionalFilters = !!selectedSubcontractor
  const itemType = options?.["item_type"] || []

  const types: CheckboxOptionType[] = useMemo(() => {
    return [{ label: "All", value: null }, ...itemType] as CheckboxOptionType[]
  }, [itemType])

  const entryTypeOptions: CheckboxOptionType[] = useMemo(() => {
    const base = options?.["entry_type"]?.length
      ? options["entry_type"]
      : ENTRY_TYPE_OPTIONS
    return [{ label: "All", value: null }, ...base] as CheckboxOptionType[]
  }, [options])

  const onFilterChangeHandler =
    (key: keyof ProjectFinancialTransactionFilters) => (value: unknown) => {
      onFilterChange({ [key]: value || null })
    }

  return (
    <Flex gap="small" vertical>
      <Flex justify="space-between" align="center">
        <Typography.Title level={4}>
          Financial Transactions Items
        </Typography.Title>

        <Space>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={onOpenColumnConfig}
            disabled={isLocked}
          >
            {t("entityTable.customize")}
          </Button>
          <ExportButton
            onClick={onExport}
            disabled={isLocked || isEmptyList || hasAdditionalFilters}
          />
        </Space>
      </Flex>

      {fetchingOptions ? (
        <Skeleton.Node active className={"h-8 w-full"} />
      ) : (
        <Flex gap={12} align="center" justify={"space-between"}>
          <Space>
            <Radio.Group
              disabled={isLocked || hasAdditionalFilters}
              value={filters?.item_type}
              onChange={(e) => onFilterChange({ item_type: e.target.value })}
              buttonStyle="solid"
              optionType={"button"}
              options={types}
            />
            <Radio.Group
              disabled={isLocked || hasAdditionalFilters}
              value={filters?.entry_type}
              onChange={(e) => onFilterChange({ entry_type: e.target.value })}
              buttonStyle="solid"
              optionType={"button"}
              options={entryTypeOptions}
            />
            {selectedSubcontractor && (
              <Tag closable onClose={onClearSubcontractor} color="blue">
                {selectedSubcontractor?.full_name || "No name provided"}
              </Tag>
            )}
          </Space>

          {/*<Space>*/}
          {/*<Select*/}
          {/*  className="w-44"*/}
          {/*  placeholder="Select type"*/}
          {/*  options={options?.["project_type"]}*/}
          {/*  showSearch*/}
          {/*  allowClear*/}
          {/*  onChange={onFilterChangeHandler("project_type")}*/}
          {/*  filterOption={(input, option) =>*/}
          {/*    (option?.label ?? "")*/}
          {/*      .toLowerCase()*/}
          {/*      .includes(input?.toLowerCase())*/}
          {/*  }*/}
          {/*/>*/}

          <SearchBar
            disabled={isLocked || hasAdditionalFilters}
            className={"md:w-1/3 flex-none"}
            placeholder="Search Items..."
            defaultValue={filters?.q || ""}
            prefix={<SearchOutlined />}
            onSearch={onFilterChangeHandler("q")}
          />
          {/*</Space>*/}
        </Flex>
      )}
    </Flex>
  )
}

interface ExportButtonProps {
  disabled?: boolean
  onClick?: () => Promise<void>
}

const ExportButton = ({ onClick, disabled }: ExportButtonProps) => {
  const [exporting, setExporting] = useState(false)

  const onClickHandler = async () => {
    try {
      setExporting(true)
      await onClick?.()
    } finally {
      setExporting(false)
    }
  }

  return (
    <Button
      size="small"
      loading={exporting}
      type="primary"
      icon={<ExportOutlined />}
      disabled={disabled}
      onClick={onClickHandler}
    >
      Export
    </Button>
  )
}
